import React, { useEffect, useState } from 'react';
import DocumentDisplay from './components/DocumentDisplay';
import Request from '@/services/util/request-service';
import ProgressUtilityService from '@/services/course/progress-utility-service';
import ActivityUtilityService from '@/services/course/lesson/activity/activity-utility-service';
import { CourseId, SubunitId, UnitId, BlockingContent } from 'course-types';
import { fileTypeFromBlob } from 'file-type';
import LockedContent from '@/common/components/LockedContent';

const propTypes = {};

interface Props {
  docId: string;
  subunitId: string | undefined;
  unitId: string | undefined;
  courseId: string;
  activityName: string;
  activityDescription: string;
}

function DocumentHandler({
  docId,
  subunitId,
  unitId,
  courseId,
  activityName,
  activityDescription,
}: Props) {
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string>();
  const [locked, setLocked] = useState<boolean>(false);
  const [blockingContent, setBlockingContent] = useState<BlockingContent>({} as BlockingContent);

  useEffect(() => {
    // Function for fetching document data from backend
    const fetchPDF = async (docId: string, subunitId?: string, unitId?: string) => {
      try {
        const response = await Request.post('content/doc/view', {
          docId: docId,
          subunitId: subunitId == '' ? undefined : (subunitId ?? undefined),
          unitId: subunitId && subunitId != '' ? undefined : (unitId ?? undefined),
        });

        if (!response) {
          throw new Error('Failed to fetch PDF');
        }

        // Determine returned type
        const contentType = response.headers.get('Content-Type') ?? '';

        // Handle as file upload
        if (contentType.includes('application/pdf')) {
          const pdfBlob = await response.blob();
          const fileType = await fileTypeFromBlob(pdfBlob);
          setPdfBlob(pdfBlob);
          if (fileType) {
            setMimeType(fileType.mime);
          }
        }
        // Handle as markdown
        else if (contentType.includes('application/json')) {
          const resBody = await response.json();
          if (!resBody.content && resBody.error) {
            // Check if this is a lock error
            if (resBody.blockingContent) {
              setLocked(true);
              setBlockingContent(resBody.blockingContent || ({} as BlockingContent));
              return;
            }
            throw Error(resBody.error);
          }

          setMarkdown(resBody.content);
          setMimeType('text/markdown');
          setLocked(false);
          setBlockingContent({} as BlockingContent);
        }
      } catch (error) {
        console.error('Error fetching PDF:', error);
      }
    };

    if (
      docId != undefined &&
      ((subunitId != undefined && subunitId != '') || (unitId != undefined && unitId != ''))
    ) {
      fetchPDF(docId, subunitId, unitId);
      onOpen();
    }
  }, [docId, subunitId, unitId]);

  const onOpen = async () => {
    // TODO:: re-pull progress if cookie not set / cleared
    //        Edge case where current set method may overwrite database progress

    const { progressCookie, activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
      courseId,
      unitId,
      subunitId,
    );

    // Define progress if undefined
    if (!activityData.document[docId]) {
      activityData.document[docId] = {};
    }

    // Set openDate in DB if undefined
    if (!activityData.document[docId]?.openDate) {
      activityData.document[docId].openDate = new Date();

      await ProgressUtilityService.updateUserCourseProgressDatabase(
        courseId as CourseId,
        activityData,
        unitId as UnitId,
        subunitId as SubunitId,
      );
    }

    // Set progress cookie with new data
    ActivityUtilityService.updateActivityProgressLocal(
      progressCookie,
      activityData,
      courseId,
      unitId,
      subunitId,
    );
  };

  if (locked) {
    return <LockedContent blockingContent={blockingContent} courseId={courseId} />;
  }

  return (
    <div className='w-full lg:max-w-[1200px] md:max-w-[720px] h-full flex flex-col gap-6 mx-auto'>
      <div className='flex-grow min-h-0 w-full'>
        <DocumentDisplay
          pdfBlob={pdfBlob}
          markdown={markdown}
          mimeType={mimeType}
          name={activityName}
        />
      </div>
      {activityDescription && activityDescription.trim().length > 0 && (
        <div className='w-full max-h-[4.5rem] overflow-y-auto break-words whitespace-pre-wrap text-base leading-6'>
          {activityDescription}
        </div>
      )}
    </div>
  );
}

DocumentHandler.propTypes = propTypes;
export default DocumentHandler;
